import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Loader2, Check, X, Crop as CropIcon } from "lucide-react";
import { api } from "@/lib/api";

const ASPECTS = [
  ["Wide 16:9", 16 / 9],
  ["Standard 4:3", 4 / 3],
  ["Square 1:1", 1],
  ["Portrait 3:4", 3 / 4],
];

const FILTERS = [
  ["none", "None", ""],
  ["warm", "Warm", "sepia(0.28) saturate(1.35) hue-rotate(-8deg) brightness(1.04) contrast(1.05)"],
  ["mono", "Mono", "grayscale(1) contrast(1.12) brightness(1.02)"],
  ["grain", "Film Grain", "sepia(0.18) saturate(0.95) contrast(1.1)"],
];

const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E")`;

function addGrain(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 32;
    d[i] += n; d[i + 1] += n; d[i + 2] += n;
  }
  ctx.putImageData(img, 0, 0);
}

async function cropUrlToBlob(imgUrl, area, filterKey, maxW = 1600) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imgUrl;
  });
  const scale = Math.min(1, maxW / area.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width * scale);
  canvas.height = Math.round(area.height * scale);
  const ctx = canvas.getContext("2d");
  const css = (FILTERS.find(([k]) => k === filterKey) || [])[2];
  ctx.filter = css || "none";
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  if (filterKey === "grain") addGrain(canvas);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
}

// source: { file } for fresh uploads OR { src } for existing library images
export const MagCropModal = ({ open, source, onClose, onDone }) => {
  const [imgUrl, setImgUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(16 / 9);
  const [filter, setFilter] = useState("none");
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState("");
  const onComplete = useCallback((_a, px) => setArea(px), []);

  useEffect(() => {
    if (!open || !source) return;
    setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); setBusy(""); setFilter("none");
    if (source.file) {
      const u = URL.createObjectURL(source.file);
      setImgUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setImgUrl(source.src || "");
  }, [open, source]);

  if (!open || !source) return null;

  const uploadBlob = async (blob, filename) => {
    const fd = new FormData();
    fd.append("file", new File([blob], filename, { type: blob.type || "image/jpeg" }));
    const r = await api.post("/mag/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const confirm = async () => {
    if (!area) return;
    setBusy("crop");
    try {
      const blob = await cropUrlToBlob(imgUrl, area, filter);
      const media = await uploadBlob(blob, filter === "none" ? "cropped.jpg" : `cropped-${filter}.jpg`);
      onDone(media);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Crop failed — try again"); }
    setBusy("");
  };

  const useOriginal = async () => {
    setBusy("orig");
    try {
      const media = await uploadBlob(source.file, source.file.name || "upload.jpg");
      onDone(media);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="mag-crop-modal">
      <div className="w-full max-w-xl rounded-2xl border border-line bg-bg-elevated p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-display font-bold text-sm">
            <CropIcon className="h-4 w-4 text-brand-amber" /> Crop &amp; Reframe
          </div>
          <button type="button" onClick={onClose} data-testid="mag-crop-cancel" className="text-ink-muted hover:text-ink-primary"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {ASPECTS.map(([label, a]) => (
            <button key={label} onClick={() => setAspect(a)} data-testid={`mag-crop-aspect-${label.split(" ")[1].replace(":", "-")}`}
              className={`rounded-full px-3 py-1 text-[11px] font-bold border ${aspect === a ? "bg-brand-amber text-black border-brand-amber" : "border-line text-ink-secondary"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative w-full h-[340px] rounded-lg overflow-hidden bg-black">
          {imgUrl && <Cropper image={imgUrl} crop={crop} zoom={zoom} aspect={aspect}
            style={{ mediaStyle: { filter: (FILTERS.find(([k]) => k === filter) || [])[2] || "none" } }}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />}
          {filter === "grain" && (
            <div className="absolute inset-0 pointer-events-none" data-testid="mag-crop-grain-overlay"
              style={{ backgroundImage: GRAIN_SVG, opacity: 0.22, mixBlendMode: "overlay" }} />
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">Look</span>
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`mag-crop-filter-${k}`}
              className={`rounded-full px-3 py-1 text-[11px] font-bold border ${filter === k ? "bg-brand-cyan text-black border-brand-cyan" : "border-line text-ink-secondary"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">Zoom</span>
          <input type="range" min={1} max={3} step={0.05} value={zoom} data-testid="mag-crop-zoom"
            onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#f59e0b]" />
        </div>
        <p className="mt-2 text-[10px] text-ink-muted">Drag to reposition · slide to zoom · pick a look. Wide 16:9 matches story cards and the homepage hero.</p>
        <div className="mt-4 flex justify-end gap-2">
          {source.file && (
            <button type="button" onClick={useOriginal} disabled={!!busy} data-testid="mag-crop-use-original"
              className="rounded-md border border-line px-4 py-2 text-xs font-semibold text-ink-secondary hover:text-ink-primary disabled:opacity-50">
              {busy === "orig" ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Skip — use original"}
            </button>
          )}
          <button type="button" onClick={confirm} disabled={!!busy || !area} data-testid="mag-crop-confirm"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
            {busy === "crop" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Use this crop
          </button>
        </div>
      </div>
    </div>
  );
};
